/**
 * Админский борд очереди: что лежит в пуле, раздача, переброс назначения.
 *
 * Раздача считается в воркере: POST /api/queue/distribute отвечает 202 и runId,
 * UI поллит статус запуска (§6.3). Синхронного ответа «раздал N» здесь нет
 * и быть не может — на 5000 элементах это минуты под advisory-локом.
 */
import { z } from 'zod';
import { assignmentStatusSchema, bigIdSchema, blockedReasonSchema, isoDateTimeSchema, phoneE164Schema, queueStatusSchema, refIdSchema, } from './common.dto.js';
/** Режим раздачи: только НДЗ владельцам, только свежие, либо оба (§6.3) */
export const distributeModeSchema = z.enum(['fresh', 'ndz', 'mixed']);
/** GET /api/queue/board — сводка пула по статусам, кругам и источникам */
export const queueBoardQuerySchema = z.object({
    sourceId: z.coerce.number().int().positive().optional(),
    roundNumber: z.coerce.number().int().min(1).max(20).optional(),
});
export const queueBoardRowSchema = z.object({
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    roundNumber: z.number().int().min(1).max(20),
    status: queueStatusSchema,
    itemsCnt: z.number().int().min(0),
    /** Сколько из них уже доступны к выдаче: available_from <= now */
    readyCnt: z.number().int().min(0),
});
/** Кто сейчас может брать работу — тот же расчёт, что использует раздача (§6.2) */
export const dispatcherCapacityRowSchema = z.object({
    dispatcherId: bigIdSchema,
    fullName: z.string(),
    capacity: z.number().int().min(0),
    allowFresh: z.boolean(),
    allowNdz: z.boolean(),
    blockedReason: blockedReasonSchema.nullable(),
});
export const queueBoardResponseSchema = z.object({
    rows: z.array(queueBoardRowSchema),
    capacity: z.array(dispatcherCapacityRowSchema),
    /** Назначений в статусе needs_label: больше 10 — повод разбираться (§6.6) */
    needsLabelCnt: z.number().int().min(0),
    /** НДЗ без движения: владелец не звонит, деньги за контакт уже потрачены */
    staleNdzCnt: z.number().int().min(0),
});
/** POST /api/queue/distribute */
export const distributeBodySchema = z.object({
    mode: distributeModeSchema,
    /** Верхняя граница запуска; больше settings.distribute.max_items_per_run сервер порежет на батчи */
    limit: z.number().int().min(1).max(20_000).optional(),
    sourceIds: z.array(refIdSchema).max(200).optional(),
    dispatcherIds: z.array(bigIdSchema).max(200).optional(),
    /**
     * Часовые пояса абонентов в минутах от UTC: 180 — Москва, 600 — Владивосток.
     * Пусто — без ограничения. Нужно, чтобы прозвонить сначала Дальний Восток,
     * пока там рабочий день, и только потом европейскую часть (§9.5).
     *
     * Это фильтр ПУЛА, а не второй способ выдать работу: окно звонка, смена,
     * лимиты и владение недозвоном остаются теми же (ADR-0006).
     */
    tzOffsets: z.array(z.number().int().min(-720).max(840)).max(24).optional(),
    /**
     * Ручные доли свежего пула по источникам (30.08.2026): «МедиаПро 60%,
     * ЛидСтор 40%». Сумма процентов — ровно 100. НДЗ долям не подчиняется,
     * источник вне списка не выдаётся вовсе.
     */
    sourceShares: z
        .array(z.object({ sourceId: refIdSchema, percent: z.number().int().min(1).max(100) }))
        .min(1)
        .max(50)
        .refine((shares) => shares.reduce((sum, s) => sum + s.percent, 0) === 100, {
        message: 'сумма долей должна быть ровно 100%',
    })
        .refine((shares) => new Set(shares.map((s) => s.sourceId)).size === shares.length, {
        message: 'источник в долях не может повторяться',
    })
        .optional(),
    /** Двойной клик и ретрай сети должны дать один запуск, а не два */
    idempotencyKey: z.string().min(8).max(120),
});
/** 202: раздача поставлена в очередь, результат смотреть по runId */
export const distributeAcceptedResponseSchema = z.object({
    runId: bigIdSchema,
    status: z.enum(['running', 'done', 'failed']),
});
/** GET /api/queue/runs/:id — то, что поллит UI после 202 */
export const distributionRunResponseSchema = z.object({
    id: bigIdSchema,
    mode: distributeModeSchema,
    status: z.enum(['running', 'done', 'failed']),
    itemsAssigned: z.number().int().min(0),
    dispatchersCnt: z.number().int().min(0),
    /**
     * Кому фактически дошло (31.08.2026): считается по назначениям запуска, а не
     * по плану. «Дошли или нет» — первый вопрос после точечной догрузки, и общий
     * счётчик itemsAssigned на него не отвечает.
     */
    byDispatcher: z.array(z.object({
        dispatcherId: bigIdSchema,
        fullName: z.string(),
        assignedCnt: z.number().int().min(0),
    })),
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    error: z.string().nullable(),
});
/**
 * POST /api/queue/distribute/preview — что раздача сделает, без записи.
 * Считается на снимке: реальный запуск может отличаться, если пул уехал.
 */
export const distributePreviewResponseSchema = z.object({
    totalPlanned: z.number().int().min(0),
    byDispatcher: z.array(z.object({ dispatcherId: bigIdSchema, fullName: z.string(), plannedCnt: z.number().int().min(0) })),
    bySource: z.array(z.object({ sourceId: refIdSchema, sourceTitle: z.string(), plannedCnt: z.number().int().min(0) })),
    /** Почему план пуст: нет смены, пустой пул, все лимиты выбраны */
    emptyReason: z.enum(['no_dispatchers_on_shift', 'empty_pool', 'limits_exhausted']).nullable(),
});
/** Строка борда назначений: с чем сейчас работают диспетчеры */
export const assignmentRowSchema = z.object({
    id: bigIdSchema,
    dispatcherId: bigIdSchema,
    dispatcherName: z.string(),
    phone: phoneE164Schema,
    sourceId: refIdSchema,
    roundNumber: z.number().int().min(1).max(20),
    status: assignmentStatusSchema,
    assignedAt: isoDateTimeSchema,
    callsCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    isReached: z.boolean(),
    labelId: refIdSchema.nullable(),
});
/**
 * GET /api/queue/assignments — без фильтра по статусу отдаются только живые
 * назначения ('open', 'called', 'needs_label'): именно их перебрасывают, а
 * закрытых за день в разы больше, и они бы вытеснили нужное из выдачи.
 */
export const assignmentsResponseSchema = z.object({ items: z.array(assignmentRowSchema) });
export const assignmentsQuerySchema = z.object({
    dispatcherId: bigIdSchema.optional(),
    status: assignmentStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
/** POST /api/queue/assignments/:id/reassign — передать контакт другому диспетчеру */
export const reassignBodySchema = z.object({
    toDispatcherId: bigIdSchema,
    reason: z.string().min(3).max(500),
});
/** POST /api/queue/assignments/:id/revoke — снять назначение и вернуть контакт в пул */
export const revokeAssignmentBodySchema = z.object({
    reason: z.string().min(3).max(500),
    /** Вернуть в общий пул, а не оставить владельцу: осознанное решение админа */
    releaseOwner: z.boolean().default(false),
});
/** POST /api/queue/items/:id/exclude — снять контакт из очереди навсегда */
export const excludeQueueItemBodySchema = z.object({
    reason: z.string().min(3).max(500),
});
/**
 * GET /api/queue/contact-requests — открытые сигналы «дайте номера» (30.08.2026).
 * Плашка на Обзоре: авто-раздача добирает сама, висящий запрос означает
 * «очередь пуста — загрузите базу». Закрывается раздачей или кнопкой.
 */
export const contactRequestsResponseSchema = z.object({
    items: z.array(z.object({
        id: bigIdSchema,
        dispatcherId: bigIdSchema,
        fullName: z.string(),
        createdAt: isoDateTimeSchema,
    })),
});
/**
 * GET /api/queue/tz-breakdown — живая очередь по часовым поясам абонентов
 * (30.08.2026): «сначала восток, пока там день» выбирается по этим цифрам,
 * а не вслепую. null в tzOffsetMin — реестр пояса не знает (звонится по
 * правилу «неизвестный = Москва», настройка окна звонка).
 */
export const queueTzBreakdownResponseSchema = z.object({
    items: z.array(z.object({
        tzOffsetMin: z.number().int().nullable(),
        readyCnt: z.number().int().min(0),
        totalCnt: z.number().int().min(0),
    })),
});
/**
 * GET /api/queue/fresh-breakdown — состав свежей очереди: источник × день прихода
 * (31.08.2026). Без этого админ видит одну цифру «519 свежих» и не может
 * сказать, сегодняшняя это закупка или залежи, и из каких меток она состоит.
 */
export const queueFreshBreakdownResponseSchema = z.object({
    items: z.array(z.object({
        sourceId: refIdSchema,
        sourceTitle: z.string(),
        /** День прихода по рабочему поясу компании, YYYY-MM-DD */
        day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        totalCnt: z.number().int().min(0),
        /** Готовы к ОБЩЕЙ раздаче прямо сейчас: available_from наступил и владельца нет */
        readyCnt: z.number().int().min(0),
        /** Закреплены за диспетчером (остаток адресной выдачи): уйдут только владельцу */
        pinnedCnt: z.number().int().min(0),
    })),
});
/**
 * GET /api/queue/rounds-today — «круги сегодня» (04.09.2026): свежие дня,
 * кому сколько роздано, что на руках и по кругам, сколько ждут круг 2/3 и когда всплывут.
 */
export const queueRoundsTodayResponseSchema = z.object({
    /** Бизнес-сутки, YYYY-MM-DD */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    freshReceived: z.number().int().min(0),
    freshUnassigned: z.number().int().min(0),
    /** Круги, встретившиеся сегодня: по ним строятся колонки таблицы */
    rounds: z.array(z.number().int().min(1)),
    dispatchers: z.array(z.object({
        dispatcherId: bigIdSchema,
        fullName: z.string(),
        assignedToday: z.number().int().min(0),
        openNow: z.number().int().min(0),
        rounds: z.array(z.object({
            round: z.number().int().min(1),
            labeled: z.number().int().min(0),
            live: z.number().int().min(0),
        })),
        /** Ждут своего круга: круги задаются round_steps и меняются без выкладки */
        waiting: z.array(z.object({
            round: z.number().int().min(2),
            count: z.number().int().min(0),
            nextAt: z.string().datetime({ offset: true }).nullable(),
        })),
        toRobot: z.number().int().min(0),
    })),
    dueByHour: z.array(z.object({
        hour: z.number().int().min(0).max(23),
        rounds: z.array(z.object({ round: z.number().int().min(2), count: z.number().int().min(0) })),
    })),
});
/** GET /api/queue/fresh-items — свежие номера поимённо: что именно лежит в очереди */
export const queueFreshItemsQuerySchema = z.object({
    sourceId: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const queueFreshItemsResponseSchema = z.object({
    items: z.array(z.object({
        id: bigIdSchema,
        phone: phoneE164Schema,
        sourceId: refIdSchema,
        sourceTitle: z.string(),
        fullName: z.string().nullable(),
        /** Пояс абонента в минутах от UTC; null — реестр не знает, звоним по Москве */
        tzOffsetMin: z.number().int().nullable(),
        /** Регион и оператор из реестра диапазонов — те же, что на карточке диспетчера */
        region: z.string().nullable(),
        operator: z.string().nullable(),
        createdAt: isoDateTimeSchema,
        /** available_from наступил: номер уже может уйти в раздачу */
        ready: z.boolean(),
        /** Закреплён за диспетчером: в общую раздачу не попадёт, уйдёт владельцу */
        pinned: z.boolean(),
    })),
    /** Всего строк под фильтром — для «показано 50 из 519» */
    totalCnt: z.number().int().min(0),
});
/** POST /api/queue/assignments/revoke-all — снять ВСЕ открытые карточки диспетчера */
export const revokeAllBodySchema = z.object({
    dispatcherId: bigIdSchema,
    /** true — вернуть и НДЗ в общий пул, не оставляя владельцу */
    releaseOwner: z.boolean().default(false),
});
/** POST /api/queue/assignments/exclude-all — убрать ВСЕ карточки диспетчера из прозвона совсем */
export const excludeAllBodySchema = z.object({
    dispatcherId: bigIdSchema,
});
/**
 * POST /api/queue/assignments/hand-off — снять все номера с диспетчера, который
 * не выйдет: карточки на руках и номера, ждущие у него следующего круга.
 * `others` — свежие в общий пул, повторы роботу; `robot` — всё роботу.
 */
export const handOffBodySchema = z.object({
    dispatcherId: bigIdSchema,
    target: z.enum(['others', 'robot']),
});
export const handOffResponseSchema = z.object({
    toPool: z.number().int().min(0),
    toRobot: z.number().int().min(0),
    skipped: z.number().int().min(0),
});
/** GET /api/queue/owned-waiting — сколько номеров ждёт каждого диспетчера в очереди */
export const ownedWaitingResponseSchema = z.object({
    items: z.array(z.object({
        dispatcherId: bigIdSchema,
        fullName: z.string(),
        waitingCnt: z.number().int().min(0),
    })),
});
//# sourceMappingURL=queue.dto.js.map